import React from "react"
import { motion } from "framer-motion"
import { Home, CheckCircle, Search, Map, Key, Coffee, Shield, PhoneCall } from "lucide-react"
import Layout from "../../components/common/Layout"
import SEO from "../../components/common/SEO"
import "./ServicePage.css"

const Accommodation = () => {
  const accommodationTypes = [
    {
      icon: <Home size={24} />,
      title: "University Halls",
      desc: "On-campus accommodation with full facilities and a vibrant student community.",
      features: ["Fully furnished rooms", "Meal plans available", "24/7 security", "Common rooms"]
    },
    {
      icon: <Key size={24} />,
      title: "Private Housing",
      desc: "Independent living with more privacy, flexibility, and tailored comfort.",
      features: ["Studio apartments", "Shared houses", "One-bedroom flats", "Family units"]
    },
    {
      icon: <Coffee size={24} />,
      title: "Homestay",
      desc: "Live with local families for deep cultural immersion and language practice.",
      features: ["Cultural experience", "Language practice", "Meals included", "Family support"]
    },
    {
      icon: <Search size={24} />,
      title: "Add-on Services",
      desc: "End-to-end support to help you settle into your new home smoothly.",
      features: ["Airport pick-up", "Contract assistance", "Roommate matching", "Settling support"]
    }
  ]

  return (
    <Layout>
      <SEO
        title="Accommodation Assistance - Student Housing Support | Welsh Education Group"
        description="Comprehensive accommodation assistance for international students. Find safe housing near your university."
        url="/services/accommodation"
      />

      <div className="service-page">
        <section className="service-hero-modern">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="hero-icon-large"
            >
              <Home size={40} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Accommodation Services
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Your second home, away from home. We find the perfect living space that matches your lifestyle and budget.
            </motion.p>
          </div>
        </section>

        <section className="service-content-modern">
          <div className="container">
            <div className="service-grid-modern">
              <div className="main-service-info">
                <h2>Verified Student Housing</h2>
                <p>
                  Finding the right place to live is crucial for your academic success. We partner with reputable providers worldwide to ensure you have access to safe, clean, and student-friendly environments near your chosen institution.
                </p>

                <div className="service-features-list">
                  {accommodationTypes.map((type, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="feature-pill"
                    >
                      <div className="feature-pill-icon">{type.icon}</div>
                      <div className="feature-pill-content">
                        <h4>{type.title}</h4>
                        <p>{type.desc}</p>
                        <ul style={{ listStyle: 'none', padding: 0, marginTop: '12px' }}>
                          {type.features.map((f, i) => (
                            <li key={i} style={{ fontSize: '13px', color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <CheckCircle size={12} color="var(--cyan-glow)" /> {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="service-sidebar-modern">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  className="sidebar-cta-card"
                >
                  <Map className="text-gradient" size={48} style={{ margin: '0 auto 24px' }} />
                  <h3>Housing Search</h3>
                  <p>Tell us your preferences and we'll send a curated list.</p>
                  <button className="sidebar-btn highlight-glow">Start Housing Search</button>
                </motion.div>

                <div className="info-card">
                  <h3 style={{ fontSize: '18px', marginBottom: '20px' }}>Estimated Costs</h3>
                  <div className="stats-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted-foreground)' }}>Univ. Halls</span>
                      <span style={{ fontWeight: 700 }}>£400 - £800</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted-foreground)' }}>Private Studio</span>
                      <span style={{ fontWeight: 700 }}>£600 - £1200</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted-foreground)' }}>Shared House</span>
                      <span style={{ fontWeight: 700 }}>£350 - £700</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default Accommodation
